// src/appRWAPromo/hooks/useMarketFeedInstant.js
import { useEffect, useMemo, useState } from "react";
import { useMarketFeed } from "./useMarketFeed";

const CACHE_KEY = "market_feed_v1";

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(streams, predictions) {
  try {
    const slim = {
      streams: (streams || []).map((x) => ({
        id: x.id,
        type: "stream",
        name: x.name,
        title: x.title,
        handle: x.handle,
        subtitle: x.subtitle,
        description: x.description,
        avatarUrl: x.avatarUrl,
        poster: x.poster,
        posterLow: x.posterLow || x.posterBlur,
        previewSrc: x.previewSrc,
        preview480: x.preview480,
        views: x.views,
        status: x.status,
        category: x.category,
        flags: x.flags,
        market: x.market,
        marketChip: x.marketChip,
        marketSub: x.marketSub,
        tags: x.tags,
        assets: x.assets,
      })),
      predictions: (predictions || []).map((x) => ({
        id: x.id,
        type: "prediction",
        name: x.name,
        title: x.title,
        handle: x.handle,
        subtitle: x.subtitle,
        description: x.description,
        avatarUrl: x.avatarUrl,
        imageSrc: x.imageSrc,
        imageLow: x.imageLow,
        poster: x.poster,
        posterLow: x.posterLow || x.posterBlur,
        yesBets: x.yesBets,
        noBets: x.noBets,
        views: x.views,
        category: x.category,
        flags: x.flags,
        top: x.top,
        market: x.market,
        marketChip: x.marketChip,
        marketSub: x.marketSub,
        tags: x.tags,
        assets: x.assets,
      })),
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: slim }));
  } catch {}
}

export function useMarketFeedInstant({ pageSize = 300 } = {}) {
  const cached = useMemo(() => (typeof window !== "undefined" ? readCache() : null), []);
  const [instant, setInstant] = useState(() => cached || { streams: [], predictions: [] });

  const live = useMarketFeed({ pageSize });

  useEffect(() => {
    if (!live.loadingStreams && !live.loadingPredictions) {
      const streams = live.streams || [];
      const predictions = live.predictions || [];
      setInstant({ streams, predictions });
      writeCache(streams, predictions);
    }
  }, [live.loadingStreams, live.loadingPredictions, live.streams, live.predictions]);

  return {
    streams: instant.streams,
    predictions: instant.predictions,
    loadingStreams: !instant.streams.length && live.loadingStreams,
    loadingPredictions: !instant.predictions.length && live.loadingPredictions,
  };
}
